import React, { useState, useEffect } from 'react';
import { authFetch } from '../auth/AuthContext';

const MATCH_INCOME_TYPES = ['DSCR','Commercial','Conventional commercial','SBA 7(a)','SBA 504','Portfolio / Blanket'];
function mNorm(n){ const v=Number(n); if(!v||isNaN(v)) return 0; return (v>0&&v<=1)?v*100:v; }
function mMoney(n){ return '$'+Math.round(Number(n)||0).toLocaleString(); }
function mStates(x){ if(Array.isArray(x)) return x.map(v=>String(v).trim().toUpperCase()).filter(Boolean); return String(x||'').split(',').map(v=>v.trim().toUpperCase()).filter(Boolean); }

function leverageEval(deal, L, p){
  const reasons=[], pass=[];
  const refi = deal.loanPurpose==='Refinance' || deal.loanPurpose==='Cash-out refinance';
  const ltvCap = refi ? p.maxLtvRefi : p.maxLtvPurchase;
  function testMax(name, val, cap){
    val=Number(val)||0; cap=(cap==null||cap==='')?null:Number(cap);
    if(val>0 && cap!=null && cap>0){ if(val>cap) reasons.push(name+' '+val+'% over max '+cap+'%'); else pass.push(name+' '+val+'% within '+cap+'%'); }
  }
  if(deal.isSecond){ testMax('CLTV', deal.cltv, mNorm(L.maxCltv)); return {reasons,pass}; }
  const lt=deal.loanType;
  if(lt==='Fix & Flip'){ testMax('LTARV', deal.ltarv, p.maxArv); testMax('LTC', deal.ltc, p.maxLtc); }
  else if(lt==='Ground-Up Construction'){ testMax('LTC', deal.ltc, p.maxLtc); testMax('LTARV', deal.ltarv, p.maxArv); }
  else if(MATCH_INCOME_TYPES.indexOf(lt)>=0){ testMax('LTV', deal.ltv, ltvCap); if(deal.dscr>0 && p.minDscr!=null && p.minDscr>0){ if(deal.dscr<p.minDscr) reasons.push('DSCR '+deal.dscr+' below min '+p.minDscr); else pass.push('DSCR '+deal.dscr+' meets '+p.minDscr); } }
  else if(lt==='Bridge'){ testMax('LTV', deal.ltv, ltvCap); testMax('LTC', deal.ltc, p.maxLtc); testMax('LTARV', deal.ltarv, p.maxArv); }
  else if(lt==='Working capital / LOC'){ /* loan amount only */ }
  else { testMax('LTV', deal.ltv, ltvCap); }
  return {reasons,pass};
}

function programEval(deal, L, p){
  if((p.loanType||'') !== deal.loanType) return {wrongType:true};
  const contradict=[], unknown=[], pass=['Loan type: '+deal.loanType];
  if(deal.propertyType){
    if(!(Array.isArray(p.propertyType) && p.propertyType.length)) unknown.push('Property type not on file');
    else if(p.propertyType.indexOf(deal.propertyType)<0) contradict.push('Property type '+deal.propertyType+' not funded');
    else pass.push(deal.propertyType);
  }
  if(deal.loanAmount>0){
    if(p.minLoan==null && p.maxLoan==null) unknown.push('Loan size not on file');
    else if(p.minLoan!=null && deal.loanAmount<p.minLoan) contradict.push('Below min loan '+mMoney(p.minLoan));
    else if(p.maxLoan!=null && deal.loanAmount>p.maxLoan) contradict.push('Above max loan '+mMoney(p.maxLoan));
    else pass.push('Loan in range');
  }
  if(deal.fico>0 && p.minFico!=null && p.minFico>0){
    if(deal.fico<p.minFico) contradict.push('FICO '+deal.fico+' below program min '+p.minFico);
    else pass.push('FICO '+deal.fico+' meets '+p.minFico);
  }
  const lev=leverageEval(deal,L,p);
  for(const r of lev.reasons) contradict.push(r);
  for(const ps of lev.pass) pass.push(ps);
  return {contradict, unknown, pass};
}

function lenderEval(deal, L){
  const contradict=[], unknown=[], pass=[];
  const states=mStates(L.states);
  if(deal.state){
    if(!states.length) unknown.push('States not on file');
    else if(states.indexOf(deal.state)<0) contradict.push('Not licensed in '+deal.state);
    else pass.push(deal.state);
  }
  if(deal.ownerOcc){
    if(!L.ownerOcc) unknown.push('Owner-occupied not on file');
    else if(L.ownerOcc==='No') contradict.push('No owner-occupied');
    else pass.push('Owner-occupied ok');
  }
  if(deal.foreignNational){
    if(!L.foreignNational) unknown.push('Foreign national not on file');
    else if(L.foreignNational==='No') contradict.push('No foreign nationals');
    else pass.push('Foreign national ok');
  }
  if(deal.borrowerIsIndividual){
    if(!L.entity) unknown.push('Entity policy not on file');
    else if(L.entity==='Entity required') contradict.push('Entity required, borrower is individual');
    else pass.push('Individual borrower ok');
  }
  if(deal.wholesale){
    if(!L.wholesale) unknown.push('Wholesale not on file');
    else if(L.wholesale==='No') contradict.push('No wholesale');
    else pass.push('Wholesale ok');
  }
  if(deal.lienPos && deal.lienPos!=='1st lien'){
    if(!(Array.isArray(L.lienPositions) && L.lienPositions.length)) unknown.push('Lien positions not on file');
    else if(L.lienPositions.indexOf(deal.lienPos)<0) contradict.push('Does not fund '+deal.lienPos);
    else pass.push(deal.lienPos);
  }
  if(deal.situations.length){
    if(!(Array.isArray(L.situations) && L.situations.length)) unknown.push('Situations not on file');
    else deal.situations.forEach(function(sit){ if(L.situations.indexOf(sit)<0) contradict.push('Does not accept '+sit); else pass.push(sit); });
  }
  if(deal.assetClasses.length){
    if(!(Array.isArray(L.specialties) && L.specialties.length)) unknown.push('Asset class not on file');
    else deal.assetClasses.forEach(function(a){ if(L.specialties.indexOf(a)<0) contradict.push('Does not fund '+a); else pass.push(a); });
  }
  if(deal.recoursePref==='Non-recourse required'){
    if(!L.recourseType) unknown.push('Recourse not on file');
    else if(L.recourseType==='Full recourse') contradict.push('Full recourse only');
    else pass.push('Non-recourse ok');
  }
  if(deal.appraisalPref==='No appraisal needed'){
    if(!L.appraisalRequired) unknown.push('Appraisal policy not on file');
    else if(L.appraisalRequired==='Yes') contradict.push('Requires appraisal');
    else pass.push('No appraisal ok');
  }
  const floor=(L.lenderMinFico===''||L.lenderMinFico==null)?0:Number(L.lenderMinFico);
  if(deal.fico>0 && floor>0 && deal.fico<floor) contradict.push('FICO '+deal.fico+' below lender floor '+floor);
  return {contradict, unknown, pass};
}

function matchLenders(deal, lenders){
  const potential=[], maybe=[], ruledout=[];
  (lenders||[]).forEach(function(L){
    const le=lenderEval(deal, L);
    let contradict=le.contradict.slice(), unknown=le.unknown.slice(), pass=le.pass.slice();
    const progs=(L.programs||[]).filter(function(p){return p.active!==false;});
    if(progs.length===0){
      unknown.push('No program criteria on file');
    } else {
      const typed=progs.map(function(p){return programEval(deal,L,p);}).filter(function(r){return !r.wrongType;});
      if(typed.length===0){
        contradict.push('No '+deal.loanType+' program on file');
      } else {
        typed.sort(function(a,b){return a.contradict.length-b.contradict.length;});
        const best=typed[0];
        contradict=contradict.concat(best.contradict);
        unknown=unknown.concat(best.unknown);
        pass=pass.concat(best.pass);
      }
    }
    const vstatus = L.verified ? 'verified' : 'unconfirmed';
    const entry={lender:L, contradict, unknown, pass, vstatus};
    if(vstatus==='verified' && contradict.length>0){ ruledout.push(entry); }
    else if(contradict.length>0 || unknown.length>0){ maybe.push(entry); }
    else { potential.push(entry); }
  });
  potential.sort(function(a,b){ return (b.vstatus==='verified'?1:0)-(a.vstatus==='verified'?1:0); });
  maybe.sort(function(a,b){ return a.contradict.length-b.contradict.length; });
  return {potential, maybe, ruledout};
}

const LOAN_TYPE_GROUPS = [
  ['Residential / bridge', ['Fix & Flip','Ground-Up Construction','Bridge','DSCR']],
  ['Land',                 ['Land','Agricultural']],
  ['Commercial',           ['Commercial','Conventional commercial','SBA 7(a)','SBA 504','Working capital / LOC','Portfolio / Blanket']],
  ['Manufactured',         ['MH Chattel','MH Land/Home']],
  ['Other',                ['2nd Mortgage','Gap / Mezzanine','Other']],
];
const LOAN_PURPOSES = ['Purchase','Refinance','Cash-out refinance','Construction','Bridge'];
const PROP_TYPES = ['SFR','Condo / Townhome','2-4 Units','Multifamily 5+','Mixed-Use','Commercial','Land / Vacant Lot','Agricultural','Manufactured / Mobile','Other'];
const LIEN_POSITIONS = ['1st lien','2nd lien'];
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

const num = x => Number(x)||0;
const fmtD = n => n?'$'+Number(n).toLocaleString():'';
const pN = x => String(x).replace(/[^0-9.]/g,'');

const EMPTY = {
  loanType:'', loanPurpose:'', state:'', propertyType:'',
  loanAmount:'', purchasePrice:'', asIsValue:'', arv:'', rehabBudget:'',
  ltv:'', fico:'', liquidity:'', lienPos:'1st lien', exit:'',
  situations:[], assetClasses:[],
  ownerOcc:false, foreignNational:false, wholesale:false,
};

const s = {
  wrap:{maxWidth:'920px'},
  card:{background:'#fff',borderRadius:'10px',border:'0.5px solid #B8D4F0',overflow:'hidden',marginBottom:'16px'},
  cardHead:{background:'#4A88C8',padding:'9px 16px',display:'flex',alignItems:'center',gap:'8px',borderBottom:'2px solid #7AADE0'},
  cardTitle:{color:'#fff',fontSize:'12px',fontWeight:700,letterSpacing:'0.04em'},
  cardBody:{padding:'16px'},
  hint:{fontSize:'12px',color:'#8a9ab5',marginBottom:'12px'},
  pasteBox:{width:'100%',minHeight:'130px',padding:'12px 14px',border:'1.5px solid #B8D4F0',borderRadius:'8px',fontSize:'13px',lineHeight:1.5,color:'#0D2545',fontFamily:'inherit',resize:'vertical',outline:'none',boxSizing:'border-box'},
  matchBtn:on=>({marginTop:'12px',padding:'10px 20px',background:on?'#154D97':'#A8B8D0',color:'#fff',border:'none',borderRadius:'7px',fontSize:'13px',fontWeight:700,cursor:on?'pointer':'default',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:'8px'}),
  err:{fontSize:'12px',color:'#c0392b',marginTop:'10px'},
  readTag:{display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'11px',color:'#2D5E0E',background:'#E4F2DA',borderRadius:'5px',padding:'4px 9px',marginBottom:'12px'},
  specGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))',gap:'12px'},
  field:{display:'flex',flexDirection:'column',gap:'4px'},
  label:{fontSize:'9px',fontWeight:700,color:'#4A88C8',letterSpacing:'0.07em',textTransform:'uppercase'},
  input:{padding:'7px 10px',border:'1.5px solid #B8D4F0',borderRadius:'6px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',boxSizing:'border-box',background:'#fff'},
  select:{padding:'7px 10px',border:'1.5px solid #B8D4F0',borderRadius:'6px',fontSize:'13px',color:'#0D2545',fontFamily:'inherit',outline:'none',width:'100%',cursor:'pointer',background:'#fff',boxSizing:'border-box'},
  flagSection:{marginTop:'16px',paddingTop:'14px',borderTop:'1px solid #EAF1FA'},
  flagLabel:{fontSize:'9px',fontWeight:700,color:'#4A88C8',letterSpacing:'0.07em',textTransform:'uppercase',marginBottom:'8px'},
  pillbox:{display:'flex',flexWrap:'wrap',gap:'6px'},
  pill:on=>({display:'inline-flex',alignItems:'center',gap:'5px',fontSize:'12px',padding:'5px 11px',borderRadius:'999px',border:'1px solid '+(on?'#154D97':'#C5D5EA'),background:on?'#154D97':'#fff',color:on?'#fff':'#0D2545',cursor:'pointer',userSelect:'none',fontWeight:on?600:400}),
  toggleRow:{display:'flex',flexWrap:'wrap',gap:'14px',marginTop:'12px'},
  checkboxRow:{display:'inline-flex',width:'fit-content',alignItems:'center',gap:'7px',fontSize:'13px',color:'#0D2545',cursor:'pointer',userSelect:'none'},
  bar:{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap',marginBottom:'12px'},
  count:{fontSize:'13px',fontWeight:700,color:'#3B6D11'},
  countLow:{fontSize:'13px',fontWeight:700,color:'#8A5A12'},
  scenario:{fontSize:'11px',color:'#8a9ab5'},
  tierPot:{display:'flex',alignItems:'center',gap:'7px',fontSize:'11px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',color:'#2D5E0E',margin:'4px 2px 8px'},
  tierMay:{display:'flex',alignItems:'center',gap:'7px',fontSize:'11px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',color:'#8A5A12',margin:'16px 2px 8px'},
  tierRem:{display:'flex',alignItems:'center',gap:'7px',fontSize:'11px',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',color:'#A23A2E',margin:'16px 2px 8px',cursor:'pointer'},
  pip:c=>({width:'9px',height:'9px',borderRadius:'50%',background:c,display:'inline-block'}),
  lcard:{border:'1px solid #C5D5EA',borderLeft:'4px solid #3B6D11',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#fff'},
  lcardMay:{border:'1px solid #EAD9BE',borderLeft:'4px solid #D98324',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#FFFDF8'},
  lcardX:{border:'1px solid #E3E9F2',borderLeft:'4px solid #C5D5EA',borderRadius:'8px',padding:'10px 12px',marginBottom:'8px',background:'#fff'},
  ltop:{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:'8px'},
  lname:{fontSize:'13.5px',fontWeight:700,color:'#154D97'},
  lnameX:{fontSize:'13.5px',fontWeight:700,color:'#5a6b85'},
  tag:{fontSize:'10px',fontWeight:700,textTransform:'uppercase',padding:'2px 8px',borderRadius:'5px'},
  tagV:{background:'#E4F2DA',color:'#2D5E0E'},
  tagU:{background:'#EAF1FA',color:'#3A6BA8'},
  contact:{fontSize:'11px',color:'#185FA5',marginTop:'4px'},
  passrow:{display:'flex',flexWrap:'wrap',gap:'4px',marginTop:'6px'},
  pchip:{fontSize:'10px',background:'#E4F2DA',color:'#2D5E0E',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  mchip:{fontSize:'10px',background:'#FDF0DC',color:'#7A4A08',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  mchipNo:{fontSize:'10px',background:'#FBE3DF',color:'#A23A2E',borderRadius:'4px',padding:'2px 7px',fontWeight:600},
  reason:{fontSize:'11.5px',color:'#9B2C2C',marginTop:'5px',display:'flex',alignItems:'center',gap:'5px'},
  empty:{fontSize:'13px',color:'#8a9ab5',padding:'10px 0'},
  sendDeal:{marginTop:'6px',padding:'9px 18px',background:'transparent',color:'#2B72C2',border:'1.5px solid #B8D4F0',borderRadius:'7px',fontSize:'12.5px',fontWeight:700,cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:'7px'},
  futureTag:{fontSize:'9px',fontWeight:700,textTransform:'uppercase',color:'#8a9ab5',background:'#EAF1FA',borderRadius:'4px',padding:'2px 7px',marginLeft:'6px'},
  msg:{fontSize:'11.5px',color:'#185FA5',background:'#E6F1FB',borderRadius:'6px',padding:'8px 10px',marginTop:'8px'},
};

function SpecSelect({label,value,onChange,options,grouped}){
  return (
    <div style={s.field}>
      <div style={s.label}>{label}</div>
      <select style={s.select} value={value||''} onChange={e=>onChange(e.target.value)}>
        <option value="">Select...</option>
        {grouped
          ? LOAN_TYPE_GROUPS.map(([g,opts])=>(
              <optgroup key={g} label={g}>{opts.map(o=><option key={o}>{o}</option>)}</optgroup>
            ))
          : options.map(o=><option key={o}>{o}</option>)}
      </select>
    </div>
  );
}

function SpecText({label,value,onChange,currency,placeholder}){
  return (
    <div style={s.field}>
      <div style={s.label}>{label}</div>
      <input style={s.input}
        value={currency?(value?fmtD(value):''):(value||'')}
        onChange={e=>onChange(currency?pN(e.target.value):e.target.value)}
        placeholder={placeholder||(currency?'$0':'')}/>
    </div>
  );
}

export default function QuickMatch(){
  const [text,setText] = useState('');
  const [reading,setReading] = useState(false);
  const [readErr,setReadErr] = useState('');
  const [hasRead,setHasRead] = useState(false);
  const [crit,setCrit] = useState(EMPTY);

  const [lenders,setLenders] = useState(null);
  const [lendersErr,setLendersErr] = useState('');
  const [showRuled,setShowRuled] = useState(false);

  const BORROWER_EMPTY = {
    borrowerFirstName:'', borrowerLastName:'', borrowerEmail:'', borrowerPhone:'',
    entityType:'', borrowerEntity:'', creditScore:'', borrowerExperience:'', foreignNational:'No',
  };
  const [showBorrower,setShowBorrower] = useState(false);
  const [bf,setBf] = useState(BORROWER_EMPTY);
  const [creating,setCreating] = useState(false);
  const [createErr,setCreateErr] = useState('');
  const [createdMsg,setCreatedMsg] = useState('');
  function setBfield(k,v){ setBf(p=>({...p,[k]:v})); }

  const [allContacts,setAllContacts] = useState([]);
  const [picked,setPicked] = useState(null);
  const [cQuery,setCQuery] = useState('');
  const [addingNew,setAddingNew] = useState(false);

  useEffect(()=>{
    authFetch('/.netlify/functions/get-contacts').then(r=>r.json()).then(d=>setAllContacts(d.records||[])).catch(()=>{});
  },[]);

  const cResults = (()=>{
    const q=cQuery.trim().toLowerCase();
    if(!q) return [];
    return allContacts.filter(c=>(c.fields?.Name||'').toLowerCase().includes(q)||(c.fields?.Email||'').toLowerCase().includes(q)).slice(0,6);
  })();

  function resetCreateFlow(){
    setShowBorrower(false); setPicked(null); setCQuery(''); setAddingNew(false);
    setBf(BORROWER_EMPTY); setCreateErr('');
  }

  async function createDeal(){
    setCreateErr(''); setCreatedMsg('');
    if(!crit.loanType){ setCreateErr('Set a loan type first.'); return; }

    let borrowerPayload;
    if(picked){
      const pf=picked.fields||{};
      borrowerPayload={
        borrowerFirstName:pf['First name']||'', borrowerLastName:pf['Last name']||'',
        borrowerEmail:pf['Email']||'', borrowerPhone:pf['Phone']||'',
        entityType:pf['Entity type']||'', borrowerEntity:pf['Company / entity']||'',
      };
      if(!borrowerPayload.borrowerEmail){ setCreateErr('That contact has no email on file, which is needed to link without duplicating. Pick another or add the email on the contact first.'); return; }
    } else if(addingNew){
      if(!bf.borrowerFirstName.trim()||!bf.borrowerLastName.trim()||!bf.borrowerEmail.trim()){
        setCreateErr('Borrower first name, last name, and email are required.'); return;
      }
      borrowerPayload={...bf};
    } else {
      setCreateErr('Find an existing borrower or add a new one first.'); return;
    }

    setCreating(true);
    try{
      const payload={
        loanType:crit.loanType, loanPurpose:crit.loanPurpose,
        propertyState:(crit.state||'').toUpperCase(), propertyType:crit.propertyType,
        lienPos:crit.lienPos||'1st lien',
        loanAmount:crit.loanAmount, purchasePrice:crit.purchasePrice,
        asIsValue:crit.asIsValue, arv:crit.arv, rehabBudget:crit.rehabBudget,
        ltv:crit.ltv, exit:crit.exit,
        situations:Array.isArray(crit.situations)?crit.situations:[],
        assetClasses:Array.isArray(crit.assetClasses)?crit.assetClasses:[],
        ...borrowerPayload,
      };
      const res=await authFetch('/.netlify/functions/submit-deal-internal',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      const data=await res.json();
      if(!res.ok||!data.success) throw new Error(data.error||'Create failed');
      setCreatedMsg('Deal created'+(data.contactCreated?' with a new borrower contact.':' and linked to '+(picked?(picked.fields?.Name||'the existing contact'):'the existing borrower contact')+'.'));
      resetCreateFlow();
    }catch(e){ setCreateErr(e.message); }
    finally{ setCreating(false); }
  }

  useEffect(()=>{
    authFetch('/.netlify/functions/list-lenders').then(r=>r.json()).then(d=>{
      if(d.error) throw new Error(d.error?.message||'load failed');
      setLenders(d.lenders||[]);
    }).catch(e=>setLendersErr('Could not load lenders: '+e.message));
  },[]);

  async function readDeal(){
    if(!text.trim()) return;
    setReading(true); setReadErr(''); setCreatedMsg('');
    try{
      const res=await authFetch('/.netlify/functions/quick-match-read',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({text})});
      const data=await res.json();
      if(!res.ok||data.error) throw new Error(data.error||'Read failed');
      const c=data.criteria||{};
      setCrit({
        ...EMPTY, ...c,
        loanAmount:c.loanAmount||'', purchasePrice:c.purchasePrice||'', asIsValue:c.asIsValue||'',
        arv:c.arv||'', rehabBudget:c.rehabBudget||'', ltv:c.ltv!=null?String(c.ltv):'',
        fico:c.fico!=null?String(c.fico):'', liquidity:c.liquidity||'',
        lienPos:c.lienPos||'1st lien', exit:c.exit||'',
        situations:Array.isArray(c.situations)?c.situations:[],
        assetClasses:Array.isArray(c.assetClasses)?c.assetClasses:[],
      });
      setHasRead(true);
    }catch(e){ setReadErr(e.message); }
    finally{ setReading(false); }
  }

  function setField(k,v){ setCrit(p=>({...p,[k]:v})); }
  function toggleArr(k,val){ setCrit(p=>{ const set=new Set(p[k]); set.has(val)?set.delete(val):set.add(val); return {...p,[k]:[...set]}; }); }

  const dealCriteria = {
    state:(crit.state||'').toUpperCase(),
    loanType:crit.loanType, loanPurpose:crit.loanPurpose,
    lienPos:crit.lienPos||'1st lien', isSecond:(crit.lienPos==='2nd lien'),
    loanAmount:num(crit.loanAmount),
    propertyType:crit.propertyType||'',
    assetClasses:Array.isArray(crit.assetClasses)?crit.assetClasses:[],
    situations:(Array.isArray(crit.situations)?crit.situations:[]).filter(x=>x!=='Wholesale'),
    wholesale:!!crit.wholesale || (Array.isArray(crit.situations)&&crit.situations.includes('Wholesale')),
    ownerOcc:!!crit.ownerOcc,
    foreignNational:!!crit.foreignNational,
    borrowerIsIndividual:false,
    recoursePref:'', appraisalPref:'',
    fico:num(crit.fico),
    ltv:parseFloat(crit.ltv)||0,
    cltv:0,
    ltarv:0,
    ltc:0,
    dscr:0,
  };

  const result = (hasRead && lenders) ? matchLenders(dealCriteria, lenders) : null;
  const potN = result?result.potential.length:0;
  const mayN = result?result.maybe.length:0;
  const remN = result?result.ruledout.length:0;

  const canMatch = !!text.trim() && !reading;

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.cardHead}><i className="ti ti-bolt" style={{color:'#fff',fontSize:'14px'}}/><div style={s.cardTitle}>DESCRIBE THE DEAL</div></div>
        <div style={s.cardBody}>
          <div style={s.hint}>Type it or paste it however it came in: a text from a wholesaler, an email, your own shorthand. It reads the scenario, including the flags that decide who will touch it, and matches against your lenders. Nothing is saved.</div>
          <textarea style={s.pasteBox} value={text} onChange={e=>setText(e.target.value)}
            placeholder="e.g. Bridge in San Diego 92128, purchase 1.15M, want 750k loan, ~65% LTV. Borrower 744 FICO, six flips done, 340k liquid. Closing in three weeks, exit is selling two owned properties."/>
          <button style={s.matchBtn(canMatch)} onClick={readDeal} disabled={!canMatch}>
            <i className={`ti ${reading?'ti-loader-2':'ti-search'}`} style={{fontSize:'13px'}}/>
            {reading?'Reading...':'Read & match'}
          </button>
          {readErr&&<div style={s.err}>{readErr}</div>}
          {lendersErr&&<div style={s.err}>{lendersErr}</div>}
        </div>
      </div>

      {hasRead&&<>
        <div style={s.card}>
          <div style={s.cardHead}><i className="ti ti-list-check" style={{color:'#fff',fontSize:'14px'}}/><div style={s.cardTitle}>WHAT IT READ</div></div>
          <div style={s.cardBody}>
            <div style={s.readTag}><i className="ti ti-robot" style={{fontSize:'12px'}}/> Pulled from your description. Fix anything wrong and it re-matches live.</div>
            <div style={s.specGrid}>
              <SpecSelect label="Loan type" value={crit.loanType} onChange={v=>setField('loanType',v)} grouped/>
              <SpecSelect label="Purpose" value={crit.loanPurpose} onChange={v=>setField('loanPurpose',v)} options={LOAN_PURPOSES}/>
              <SpecSelect label="State" value={crit.state} onChange={v=>setField('state',v)} options={STATES}/>
              <SpecSelect label="Property type" value={crit.propertyType} onChange={v=>setField('propertyType',v)} options={PROP_TYPES}/>
              <SpecText label="Loan amount" value={crit.loanAmount} onChange={v=>setField('loanAmount',v)} currency/>
              <SpecText label="Purchase price" value={crit.purchasePrice} onChange={v=>setField('purchasePrice',v)} currency/>
              <SpecText label="As-is value" value={crit.asIsValue} onChange={v=>setField('asIsValue',v)} currency/>
              <SpecText label="ARV" value={crit.arv} onChange={v=>setField('arv',v)} currency/>
              <SpecText label="LTV %" value={crit.ltv} onChange={v=>setField('ltv',v)}/>
              <SpecText label="FICO" value={crit.fico} onChange={v=>setField('fico',v)}/>
              <SpecText label="Liquidity" value={crit.liquidity} onChange={v=>setField('liquidity',v)} currency/>
              <SpecSelect label="Lien position" value={crit.lienPos} onChange={v=>setField('lienPos',v)} options={LIEN_POSITIONS}/>
              <SpecText label="Exit" value={crit.exit} onChange={v=>setField('exit',v)}/>
            </div>

            <div style={s.flagSection}>
              <div style={s.flagLabel}>Situation flags (click to add or remove)</div>
              <div style={s.pillbox}>
                {SITUATIONS_LIST.map(sit=>{
                  const on=(crit.situations||[]).includes(sit);
                  return <span key={sit} style={s.pill(on)} onClick={()=>toggleArr('situations',sit)}>{on&&<i className="ti ti-check" style={{fontSize:'11px'}}/>}{sit}</span>;
                })}
              </div>
            </div>

            <div style={s.flagSection}>
              <div style={s.flagLabel}>Asset class flags (click to add or remove)</div>
              <div style={s.pillbox}>
                {ASSET_CLASS_LIST.map(a=>{
                  const on=(crit.assetClasses||[]).includes(a);
                  return <span key={a} style={s.pill(on)} onClick={()=>toggleArr('assetClasses',a)}>{on&&<i className="ti ti-check" style={{fontSize:'11px'}}/>}{a}</span>;
                })}
              </div>
            </div>

            <div style={s.toggleRow}>
              <label style={s.checkboxRow}><input type="checkbox" checked={!!crit.wholesale} onChange={e=>setField('wholesale',e.target.checked)} style={{accentColor:'#2B72C2',width:'15px',height:'15px'}}/> Wholesale / assignment</label>
              <label style={s.checkboxRow}><input type="checkbox" checked={!!crit.ownerOcc} onChange={e=>setField('ownerOcc',e.target.checked)} style={{accentColor:'#2B72C2',width:'15px',height:'15px'}}/> Owner-occupied</label>
              <label style={s.checkboxRow}><input type="checkbox" checked={!!crit.foreignNational} onChange={e=>setField('foreignNational',e.target.checked)} style={{accentColor:'#2B72C2',width:'15px',height:'15px'}}/> Foreign national</label>
            </div>
          </div>
        </div>

        <div style={s.card}>
          <div style={s.cardHead}><i className="ti ti-target-arrow" style={{color:'#fff',fontSize:'14px'}}/><div style={s.cardTitle}>MATCHES</div></div>
          <div style={s.cardBody}>
            {!crit.loanType ? (
              <div style={s.empty}>Set a loan type above to match.</div>
            ) : !lenders ? (
              <div style={s.empty}>Loading lenders...</div>
            ) : !result ? (
              <div style={s.empty}>No result yet.</div>
            ) : (
            <>
              <div style={s.bar}>
                <span style={potN>0?s.count:s.countLow}>{potN} potential &middot; {mayN} maybe</span>
                {!showBorrower && <button style={s.sendDeal} onClick={()=>{setShowBorrower(true);setCreatedMsg('');setCreateErr('');}}>
                  <i className="ti ti-file-plus" style={{fontSize:'13px'}}/> Start a deal from this
                </button>}
              </div>
              <div style={{...s.scenario,marginBottom:'12px'}}>{crit.loanType}{crit.loanPurpose?' \u00b7 '+crit.loanPurpose:''} &middot; {crit.lienPos} &middot; {(crit.state||'?').toUpperCase()}</div>

              {createdMsg&&<div style={{...s.msg,background:'#E4F2DA',color:'#2D5E0E',marginBottom:'12px'}}>{createdMsg}</div>}

              {showBorrower && (
                <div style={{border:'1px solid #C5D5EA',borderRadius:'9px',padding:'14px',background:'#F7FAFE',marginBottom:'16px'}}>
                  <div style={{fontSize:'11px',fontWeight:700,color:'#154D97',letterSpacing:'0.05em',textTransform:'uppercase',marginBottom:'4px'}}>Borrower</div>
                  <div style={{fontSize:'11px',color:'#8a9ab5',marginBottom:'12px'}}>The deal specs above carry over. Find an existing borrower or add a new one.</div>

                  {picked ? (
                    <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'12px'}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:'5px',background:'#E6F1FB',color:'#185FA5',fontSize:'12px',fontWeight:600,padding:'5px 10px',borderRadius:'6px'}}><i className="ti ti-user" style={{fontSize:'12px'}}/> {picked.fields?.Name||'Selected contact'}</span>
                      <span style={{fontSize:'11px',color:'#8a9ab5',cursor:'pointer',textDecoration:'underline'}} onClick={()=>{setPicked(null);setCQuery('');}}>change</span>
                    </div>
                  ) : addingNew ? (
                    <>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(150px,1fr))',gap:'10px'}}>
                        <div style={s.field}><div style={s.label}>First name *</div><input style={s.input} value={bf.borrowerFirstName} onChange={e=>setBfield('borrowerFirstName',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Last name *</div><input style={s.input} value={bf.borrowerLastName} onChange={e=>setBfield('borrowerLastName',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Email *</div><input style={s.input} type="email" value={bf.borrowerEmail} onChange={e=>setBfield('borrowerEmail',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Phone</div><input style={s.input} type="tel" value={bf.borrowerPhone} onChange={e=>setBfield('borrowerPhone',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Entity type</div><select style={s.select} value={bf.entityType} onChange={e=>setBfield('entityType',e.target.value)}><option value="">Select...</option>{['Individual','LLC','Corporation','Partnership','Trust','Other'].map(o=><option key={o}>{o}</option>)}</select></div>
                        <div style={s.field}><div style={s.label}>Entity name</div><input style={s.input} value={bf.borrowerEntity} onChange={e=>setBfield('borrowerEntity',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>FICO</div><input style={s.input} type="number" value={bf.creditScore} onChange={e=>setBfield('creditScore',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Completed projects</div><input style={s.input} type="number" value={bf.borrowerExperience} onChange={e=>setBfield('borrowerExperience',e.target.value)}/></div>
                        <div style={s.field}><div style={s.label}>Foreign national</div><select style={s.select} value={bf.foreignNational} onChange={e=>setBfield('foreignNational',e.target.value)}><option>No</option><option>Yes</option></select></div>
                      </div>
                      <div style={{fontSize:'11px',color:'#8a9ab5',cursor:'pointer',textDecoration:'underline',marginTop:'8px'}} onClick={()=>{setAddingNew(false);setBf(BORROWER_EMPTY);}}>back to search</div>
                    </>
                  ) : (
                    <div>
                      <input style={s.input} autoFocus placeholder="Search contacts by name or email..." value={cQuery} onChange={e=>setCQuery(e.target.value)}/>
                      <div style={{border:'1px solid #C5D5EA',borderRadius:'7px',background:'#fff',marginTop:'4px',overflow:'hidden'}}>
                        {cResults.map(c=>(
                          <div key={c.id} style={{padding:'9px 12px',borderBottom:'0.5px solid #EAF1FA',cursor:'pointer'}} onClick={()=>{setPicked(c);setCQuery('');}}>
                            <div style={{fontSize:'13px',fontWeight:500,color:'#0D2545'}}>{c.fields?.Name||'Unnamed'}</div>
                            <div style={{fontSize:'11px',color:'#8a9ab5'}}>{[c.fields?.Email,c.fields?.['Company / entity']].filter(Boolean).join(' \u00b7 ')}</div>
                          </div>
                        ))}
                        {cQuery.trim()&&cResults.length===0&&<div style={{padding:'9px 12px',fontSize:'12px',color:'#8a9ab5',fontStyle:'italic',borderBottom:'0.5px solid #EAF1FA'}}>No matching contacts.</div>}
                        <div style={{padding:'10px 12px',background:'#EAF1FA',display:'flex',alignItems:'center',gap:'6px',cursor:'pointer'}} onClick={()=>{const parts=cQuery.trim().split(/\s+/);setBf({...BORROWER_EMPTY,borrowerFirstName:parts[0]||'',borrowerLastName:parts.slice(1).join(' ')||''});setAddingNew(true);}}>
                          <i className="ti ti-user-plus" style={{fontSize:'14px',color:'#2B72C2'}}/>
                          <span style={{fontSize:'12px',fontWeight:600,color:'#2B72C2'}}>{cQuery.trim()?`Create new contact: "${cQuery.trim()}"`:'Create new contact'}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {createErr&&<div style={s.err}>{createErr}</div>}
                  <div style={{display:'flex',gap:'8px',marginTop:'12px'}}>
                    <button style={s.matchBtn(!creating&&(picked||addingNew))} onClick={createDeal} disabled={creating||(!picked&&!addingNew)}>
                      <i className={`ti ${creating?'ti-loader-2':'ti-check'}`} style={{fontSize:'13px'}}/> {creating?'Creating...':'Create deal'}
                    </button>
                    <button style={s.sendDeal} onClick={resetCreateFlow} disabled={creating}>Cancel</button>
                  </div>
                </div>
              )}

              {potN>0&&<div style={s.tierPot}><span style={s.pip('#3B6D11')}/> Potential &middot; data matches</div>}
              {result.potential.map(o=>{
                const ae=o.lender.aeName||'';
                const email=o.lender.submissionEmail||o.lender.aeEmail||o.lender.lenderEmail||'';
                return (
                  <div key={o.lender.id} style={s.lcard}>
                    <div style={s.ltop}>
                      <span style={s.lname}>{o.lender.name}</span>
                      <span style={{...s.tag,...(o.vstatus==='verified'?s.tagV:s.tagU)}}>{o.vstatus==='verified'?'Verified':'Unconfirmed'}</span>
                    </div>
                    {(ae||email)&&<div style={s.contact}><i className="ti ti-user" style={{fontSize:'11px'}}/> {ae}{ae&&email?' \u00b7 ':''}{email}</div>}
                    <div style={s.passrow}>{(o.pass||[]).map((t,i)=><span key={i} style={s.pchip}><i className="ti ti-check" style={{fontSize:'9px'}}/> {t}</span>)}</div>
                  </div>
                );
              })}

              {mayN>0&&<div style={s.tierMay}><span style={s.pip('#D98324')}/> Maybe &middot; unconfirmed or unknown</div>}
              {result.maybe.map(o=>{
                const ae=o.lender.aeName||'';
                const email=o.lender.submissionEmail||o.lender.aeEmail||o.lender.lenderEmail||'';
                return (
                  <div key={o.lender.id} style={s.lcardMay}>
                    <div style={s.ltop}>
                      <span style={s.lname}>{o.lender.name}</span>
                      <span style={{...s.tag,...(o.vstatus==='verified'?s.tagV:s.tagU)}}>{o.vstatus==='verified'?'Verified':'Unconfirmed'}</span>
                    </div>
                    {(ae||email)&&<div style={s.contact}><i className="ti ti-user" style={{fontSize:'11px'}}/> {ae}{ae&&email?' \u00b7 ':''}{email}</div>}
                    <div style={s.passrow}>
                      {(o.contradict||[]).map((t,i)=><span key={'c'+i} style={s.mchipNo}>{t}</span>)}
                      {(o.unknown||[]).map((t,i)=><span key={'u'+i} style={s.mchip}>{t}</span>)}
                    </div>
                  </div>
                );
              })}

              {remN>0&&<div style={s.tierRem} onClick={()=>setShowRuled(v=>!v)}><span style={s.pip('#C0392B')}/> Ruled out &middot; verified no &middot; {remN} <span style={{textDecoration:'underline',marginLeft:'4px',fontWeight:600}}>{showRuled?'hide':'show'}</span></div>}
              {showRuled&&result.ruledout.map(o=>(
                <div key={o.lender.id} style={s.lcardX}>
                  <div style={s.ltop}>
                    <span style={s.lnameX}>{o.lender.name}</span>
                    <span style={{...s.tag,...s.tagV}}>Verified</span>
                  </div>
                  {o.contradict.map((r,i)=><div key={i} style={s.reason}><i className="ti ti-x"/> {r}</div>)}
                </div>
              ))}
            </>
            )}
          </div>
        </div>
      </>}
    </div>
  );
}

const SITUATIONS_LIST = ['Auction / courthouse-step','REO / bank-owned','Foreclosure bailout','Short sale','Tax-lien / tax-deed','Probate / estate / inherited','Trust-held (rev/irrev)','Partner buyout','Cash-out business purpose','Delayed financing','Seller 2nds / land contracts','Wholetail','Wholesale'];
const ASSET_CLASS_LIST = ['Cannabis','Senior/assisted living','Hotels/hospitality','Self-storage','Mobile/manufactured home parks','Religious / faith-based','Gas station / C-store','Automotive (car wash / repair / dealership)','Restaurant / bar / nightlife','Daycare / education','Condo conversion','Industrial/warehouse','Retail','Office','Medical/healthcare','Special-purpose','Non-warrantable condos','Short-term/Airbnb','Condotels','Student housing','RV parks','Cold storage','Distribution center','Flex space','Wineries','Marinas','Campgrounds','Funeral homes','Event venues','Parking structures','Gym / fitness'];
